import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { Badge, Button, Card, COLORS, Empty, Field, Form, Input, Page, Textarea } from "../ui.js";

interface Course {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  isPublished: boolean;
  _count?: { modules: number };
}

export default function Courses() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["courses"],
    queryFn: () => api.get<{ items: Course[] }>("/courses"),
  });

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () => api.post<Course>("/courses", { title, subtitle, description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      setTitle("");
      setSubtitle("");
      setDescription("");
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/courses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["courses"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <Page title="Cours">
      <p style={{ color: COLORS.muted }}>
        Vos formations. Cliquez le titre pour éditer la structure (Modules + Leçons) et le
        contenu de chaque leçon.
      </p>

      {!creating ? (
        <Button onClick={() => setCreating(true)}>+ Nouveau Cours</Button>
      ) : (
        <Card>
          <Form onSubmit={onSubmit}>
            <Field label="Titre">
              <Input required value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </Field>
            <Field label="Sous-titre">
              <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
            </Field>
            <Field label="Description">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Création…" : "Créer"}
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Annuler
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <div style={{ marginTop: 24 }}>
        {list.data?.items.length === 0 && <Empty>Aucun Cours. Créez le premier.</Empty>}
        {list.data?.items.map((c) => (
          <Card key={c.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <div style={{ flex: 1 }}>
                <Link
                  to={`/courses/${c.id}`}
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: COLORS.text,
                    textDecoration: "none",
                  }}
                >
                  {c.title}
                </Link>
                {c.subtitle && (
                  <div style={{ color: COLORS.muted, fontSize: 14, marginTop: 4 }}>{c.subtitle}</div>
                )}
                <div style={{ marginTop: 8, fontSize: 13, color: COLORS.muted }}>
                  {c._count?.modules ?? 0} Module(s)
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge kind={c.isPublished ? "ok" : "neutral"}>
                  {c.isPublished ? "Publié" : "Brouillon"}
                </Badge>
                <Link
                  to={`/courses/${c.id}`}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    background: COLORS.accent,
                    color: "#fff",
                    borderRadius: 6,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Éditer
                </Link>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Supprimer "${c.title}" ?`)) del.mutate(c.id);
                  }}
                  style={{ padding: "6px 12px", fontSize: 12 }}
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}
